"use client"

import { useMemo, useState } from "react"
import Link from "next/link"
import {
  ArrowRight,
  Check,
  Clipboard,
  Code2,
  Copy,
  Database,
  FlaskConical,
  LayoutTemplate,
  Search,
  Server,
  Smartphone,
  Sparkles,
  Terminal,
} from "lucide-react"

import { cn } from "@/lib/utils"
import { useSmoothScroll } from "@/hooks/use-smooth-scroll"
import { Badge } from "@/components/ui/badge"
import { buttonVariants } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { ScrollReveal } from "@/components/scroll-reveal"

import { Footer } from "../home/footer"
import { Header } from "../home/header"
import {
  LIBRARY_SNIPPETS,
  SNIPPET_CATEGORIES,
  type LibrarySnippet,
  type SnippetCategory,
} from "./data"

const categoryIcons: Record<SnippetCategory, typeof Code2> = {
  "Competitive Programming": Terminal,
  Frontend: LayoutTemplate,
  Backend: Server,
  "App Development": Smartphone,
  Database,
  Testing: FlaskConical,
}

function generatorHref(snippet: LibrarySnippet) {
  const params = new URLSearchParams({
    format: "vscode",
    description: snippet.title,
    tabTrigger: snippet.trigger,
    snippet: snippet.code,
    scope: snippet.scope,
  })

  return `/generate?${params.toString()}`
}

export function SnippetsPage() {
  const handleSmoothScroll = useSmoothScroll()
  const [category, setCategory] =
    useState<(typeof SNIPPET_CATEGORIES)[number]>("All")
  const [query, setQuery] = useState("")
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const snippets = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()
    return LIBRARY_SNIPPETS.filter((snippet) => {
      const matchesCategory =
        category === "All" || snippet.category === category
      const matchesQuery =
        !normalizedQuery ||
        [
          snippet.title,
          snippet.description,
          snippet.language,
          snippet.trigger,
          snippet.category,
        ].some((value) => value.toLowerCase().includes(normalizedQuery))
      return matchesCategory && matchesQuery
    })
  }, [category, query])

  async function copySnippet(snippet: LibrarySnippet) {
    await navigator.clipboard.writeText(snippet.code)
    setCopiedId(snippet.id)
    window.setTimeout(() => setCopiedId(null), 1600)
  }

  return (
    <div className="bg-background min-h-screen">
      <Header />
      <main>
        <section className="relative flex min-h-svh items-center overflow-hidden border-b py-20 sm:py-24">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-0"
          >
            <div className="bg-primary/10 absolute top-0 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full blur-3xl" />
            <div className="absolute inset-0 bg-[linear-gradient(to_bottom,transparent,transparent_70%,var(--background))]" />
          </div>
          <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
            <ScrollReveal y={14}>
              <Badge
                variant="outline"
                className="border-primary/20 bg-primary/5 text-primary rounded-full px-3.5 py-1.5"
              >
                <Sparkles className="size-3.5" />
                Copy. Customize. Ship.
              </Badge>
            </ScrollReveal>
            <ScrollReveal delay={0.06} y={18}>
              <h1 className="mt-6 text-4xl leading-tight font-semibold tracking-[-0.04em] text-balance sm:text-6xl">
                Proven snippets.
                <span className="text-primary block">
                  Ready for your workflow.
                </span>
              </h1>
            </ScrollReveal>
            <ScrollReveal delay={0.12} y={18}>
              <p className="text-muted-foreground mx-auto mt-6 max-w-2xl text-base leading-relaxed text-balance sm:text-lg">
                Start from practical code for contests, frontend, backend,
                mobile, databases, and tests. Customize any snippet for your
                IDE.
              </p>
            </ScrollReveal>
            <ScrollReveal delay={0.18} y={16}>
              <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => handleSmoothScroll("library")}
                  className={buttonVariants({
                    variant: "default",
                    size: "lg",
                    className:
                      "w-fit shadow-[0_0_24px_color-mix(in_oklab,var(--primary)_55%,transparent)] transition-[color,background-color,box-shadow,transform] hover:-translate-y-0.5 hover:shadow-[0_0_36px_color-mix(in_oklab,var(--primary)_75%,transparent)]",
                  })}
                >
                  Browse snippets
                  <ArrowRight />
                </button>
                <Link
                  href="/guides"
                  className={buttonVariants({
                    variant: "outline",
                    size: "lg",
                    className: "w-fit",
                  })}
                >
                  IDE setup guides
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>

        <section id="library" className="scroll-mt-20 py-20 sm:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
              <ScrollReveal y={16} className="max-w-2xl">
                <div>
                  <p className="text-primary text-sm font-medium">
                    Snippet library
                  </p>
                  <h2 className="mt-2 text-3xl font-semibold tracking-tight sm:text-4xl">
                    Useful code, ready to adapt
                  </h2>
                  <p className="text-muted-foreground mt-3">
                    Copy source directly or open it in generator for your IDE.
                  </p>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={0.08} y={14} className="w-full md:max-w-xs">
                <div className="relative">
                  <Search className="text-muted-foreground absolute top-1/2 left-3 size-4 -translate-y-1/2" />
                  <Input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search snippets..."
                    className="pl-9"
                    aria-label="Search snippets"
                  />
                </div>
              </ScrollReveal>
            </div>

            <ScrollReveal delay={0.12} y={14}>
              <div
                className="no-scrollbar mt-8 flex gap-2 overflow-x-auto pb-1"
                aria-label="Filter by category"
              >
                {SNIPPET_CATEGORIES.map((item) => (
                  <button
                    key={item}
                    type="button"
                    onClick={() => setCategory(item)}
                    aria-pressed={category === item}
                    className={cn(
                      "shrink-0 rounded-full border px-4 py-2 text-sm font-medium transition-colors",
                      category === item
                        ? "border-primary bg-primary text-primary-foreground"
                        : "bg-background text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </ScrollReveal>

            {snippets.length ? (
              <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {snippets.map((snippet, index) => {
                  const Icon = categoryIcons[snippet.category]
                  const copied = copiedId === snippet.id

                  return (
                    <ScrollReveal
                      key={snippet.id}
                      delay={(index % 3) * 0.06}
                      y={18}
                      className="h-full min-w-0"
                    >
                      <article className="bg-card flex h-full min-w-0 flex-col overflow-hidden rounded-2xl border">
                        <div className="flex items-start gap-3 p-5">
                          <div className="bg-primary/10 text-primary flex size-10 shrink-0 items-center justify-center rounded-xl">
                            <Icon className="size-5" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold">{snippet.title}</h3>
                            <p className="text-muted-foreground mt-1 text-sm leading-relaxed">
                              {snippet.description}
                            </p>
                          </div>
                        </div>

                        <Dialog>
                          <DialogTrigger asChild>
                            <div
                              role="button"
                              tabIndex={0}
                              aria-label={`View full code for ${snippet.title}`}
                              onKeyDown={(event) => {
                                if (
                                  event.key === "Enter" ||
                                  event.key === " "
                                ) {
                                  event.preventDefault()
                                  event.currentTarget.click()
                                }
                              }}
                              className="bg-muted/60 focus-visible:ring-ring hover:border-primary/50 relative mx-3 min-h-48 flex-1 cursor-pointer overflow-hidden rounded-xl border transition-colors focus-visible:ring-2 focus-visible:outline-none"
                            >
                              <div className="text-muted-foreground flex items-center justify-between border-b px-3 py-2 text-[11px]">
                                <span>{snippet.language}</span>
                                <code className="font-mono">
                                  {snippet.trigger}
                                </code>
                              </div>
                              <pre className="max-h-52 overflow-hidden p-4 font-mono text-xs leading-relaxed">
                                <code>{snippet.code}</code>
                              </pre>
                              <div className="from-muted/0 via-muted/90 to-muted pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b" />
                            </div>
                          </DialogTrigger>
                          <DialogContent
                            side="bottom"
                            className="max-h-[90svh] sm:max-w-4xl!"
                          >
                            <DialogHeader>
                              <DialogTitle>{snippet.title}</DialogTitle>
                              <DialogDescription>
                                {snippet.language} · Trigger:{" "}
                                <code className="text-foreground font-mono">
                                  {snippet.trigger}
                                </code>
                              </DialogDescription>
                            </DialogHeader>
                            <pre className="bg-muted/60 max-h-[60svh] max-w-full overflow-auto rounded-xl border p-4 font-mono text-xs leading-relaxed sm:text-sm">
                              <code>{snippet.code}</code>
                            </pre>
                            <DialogFooter className="gap-2 sm:space-x-0">
                              <button
                                type="button"
                                onClick={() => copySnippet(snippet)}
                                className={buttonVariants({
                                  variant: "outline",
                                })}
                              >
                                {copied ? (
                                  <Check className="text-emerald-500" />
                                ) : (
                                  <Copy />
                                )}
                                {copied ? "Copied" : "Copy code"}
                              </button>
                              <Link
                                href={generatorHref(snippet)}
                                className={buttonVariants({
                                  variant: "default",
                                })}
                              >
                                <Clipboard />
                                Customize
                              </Link>
                            </DialogFooter>
                          </DialogContent>
                        </Dialog>

                        <div className="grid grid-cols-2 gap-2 p-3">
                          <button
                            type="button"
                            onClick={() => copySnippet(snippet)}
                            className={buttonVariants({
                              variant: "outline",
                              size: "sm",
                            })}
                          >
                            {copied ? (
                              <Check className="text-emerald-500" />
                            ) : (
                              <Copy />
                            )}
                            {copied ? "Copied" : "Copy code"}
                          </button>
                          <Link
                            href={generatorHref(snippet)}
                            className={buttonVariants({
                              variant: "default",
                              size: "sm",
                            })}
                          >
                            <Clipboard />
                            Customize
                          </Link>
                        </div>
                      </article>
                    </ScrollReveal>
                  )
                })}
              </div>
            ) : (
              <div className="mt-8 rounded-2xl border border-dashed py-16 text-center">
                <Code2 className="text-muted-foreground mx-auto size-8" />
                <p className="mt-3 font-medium">No matching snippets</p>
                <button
                  type="button"
                  onClick={() => {
                    setQuery("")
                    setCategory("All")
                  }}
                  className="text-primary mt-2 text-sm hover:underline"
                >
                  Clear filters
                </button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  )
}
